import { Eye } from "lucide-react"
import type { TableAction, BulkAction } from '@shared/types/data-table'
import type { Inventory } from '@features/in-stores/types/inventory.types'
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"

interface InventorySpecificHandlers {
  onView?: (inventory: Inventory) => void
  onPreview?: (inventory: Inventory) => void
  onActivate?: (inventory: Inventory) => void
  onReturn?: (inventory: Inventory) => void
  onBatchFlag?: (ids: string[]) => void
  onBatchMoveToStore?: (ids: string[]) => void
}

export type InventoryActionHandlers = BaseActionHandlers<Inventory> & InventorySpecificHandlers

export const getInventoryActions = (handlers: InventoryActionHandlers = {}) => {
  const baseActions = createBaseActions('Inventory', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: false,
    showAddNote: false,
    showCopyId: true,
    showExport: true,
  })

  const inventorySpecificActions = {
    bulkActions: [
      {
        key: "flag",
        label: "Flag Selected",
        onClick: (ids: string[]) => handlers.onBatchFlag?.(ids),
      },
      {
        key: "move-to-store",
        label: "Move to Store",
        onClick: (ids: string[]) => handlers.onBatchMoveToStore?.(ids),
      },
    ] as BulkAction[],
    rowActions: [
      {
        key: "preview",
        label: "Preview",
        icon: <Eye className="h-4 w-4" />,
        onClick: (inventory: Inventory) => handlers.onPreview?.(inventory),
      },
      {
        key: "activate",
        label: "Activate",
        onClick: (inventory: Inventory) => handlers.onActivate?.(inventory),
      },
      {
        key: "return",
        label: "Return",
        onClick: (inventory: Inventory) => handlers.onReturn?.(inventory),
      },
    ] satisfies TableAction<Inventory>[],
  }

  return mergeActions(baseActions, inventorySpecificActions)
}

export const getInventoryBulkActions = (handlers: InventoryActionHandlers = {}): BulkAction[] => 
  getInventoryActions(handlers).bulkActions

export const getInventoryRowActions = (handlers: InventoryActionHandlers = {}): TableAction<Inventory>[] => 
  getInventoryActions(handlers).rowActions

export const getInventoryHoverActions = (): TableAction<Inventory>[] => []